import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find(a => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <ScrollToTop />
        <Navbar />
        <div className="pt-20 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter(a => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.imageUrl,
    articleSection: article.category,
    wordCount: article.content.replace(/<[^>]*>/g, '').length,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://xinyugongming.com/' },
      { '@type': 'ListItem', position: 2, name: t('category.title'), item: `https://xinyugongming.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <ScrollToTop />
        <Navbar />

        <article className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: t('breadcrumb.home'), href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            {/* Article Header */}
            <header className="mb-10">
              <span className="inline-block bg-accent-100 text-accent-700 text-xs font-medium px-3 py-1 rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{t('article.publishedOn')} {article.publishedAt}</span>
                <span>{t('article.author')}: {article.author}</span>
                <span>{article.readTime} {t('article.minRead')}</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full h-80 md:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-lg max-w-none mb-12 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t border-background-200">
              {article.tags.map(tag => (
                <span key={tag} className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pt-8 border-t border-background-200">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  {t('article.related')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map(ra => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </section>
            )}

            {/* Back Link */}
            <div className="mt-10 text-center">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium cursor-pointer text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                {t('article.backToList')}
              </Link>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}